import Fill from '../../atoms/container/progressBarFill';
import Focus from '../../../../shared/types/enums/focus';
import Pointer from '../../atoms/container/progressBarPointer';
import Remainder from '../../atoms/container/progressBarRemainder';
import Time from './hourAndMinutes';
import { ProgresBarContentProps as Props } from '../../../../shared/types/props/progressBar';
import { focusSelector } from '../../../../shared/store/slices/focus.slice';
import { magicRemoteSelector } from '../../../../shared/store/slices/magicRemote.slice';
import { useAppDispatch, useAppSelector } from '../../../../shared/store/hooks';
import { useEffect, useMemo } from 'react';
import { setProgressPercentage } from '@shared/store/slices/media.slice';

const MINIMUM_LIVE_OFFSET = 60;

/**
 * Progress bar content component that displays playback progress, time indicators, and live offset.
 * Handles both VOD and live content with different display modes.
 *
 * @param {Props} props - Progress bar configuration including duration, position, live state, etc.
 * @returns {JSX.Element} Complete progress bar with fill, pointer, and time displays
 */
function ProgressBarContent({
  animate = true,
  duration,
  live = true,
  liveProgress,
  pointer,
  position,
  progress,
  timeOnTop = false,
}: Props) {
  const dispatch = useAppDispatch();
  const { focus } = useAppSelector(focusSelector);
  const { enabled } = useAppSelector(magicRemoteSelector);
  const focused = focus !== Focus.NEXT_EPISODE || enabled;

  const liveOffset = useMemo(() => {
    if (live && pointer) {
      const offset = (Date.now() - pointer) / 1000;
      return offset;
    }
    return 0;
  }, [live, pointer]);

  useEffect(() => {
    if (progress) {
      dispatch(setProgressPercentage(progress));
    }
  }, [progress]);

  return (
    <>
      <Time
        live={live}
        position={timeOnTop ? 'left' : 'start'}
        value={position}
      />
      <Remainder>
        {live && liveProgress && liveOffset > MINIMUM_LIVE_OFFSET ? (
          <Fill
            animation={animate ? 'progress-update' : ''}
            fillRed={true}
            value={liveProgress}
            id={'fill'}
          />
        ) : null}
        <Fill
          animation={animate ? 'progress-update' : ''}
          value={Math.min(progress ?? 100, 100)}
        >
          {focused ? (
            <Pointer id={'pointer'}>
              {pointer !== undefined ? (
                <Time live={live} position={'time'} value={pointer} />
              ) : null}
            </Pointer>
          ) : null}
        </Fill>
      </Remainder>
      <Time
        live={live}
        position={timeOnTop ? 'right' : 'end'}
        value={duration}
      />
    </>
  );
}

export default ProgressBarContent;
